import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Crop } from "lucide-react";

const RATIOS = [{ k: "16:9", v: 16 / 9, label: "16:9 testata" }, { k: "16:10", v: 16 / 10, label: "16:10 carosello" }, { k: "3:2", v: 3 / 2, label: "3:2" }, { k: "pass", v: 1125 / 432, label: "Striscia pass Wallet" }];

// Ritaglio immagine (pan con dito/mouse + zoom) con formato fisso → Blob JPEG. onDone(blob|null) ; null = usa originale
export default function ImageCropDialog({ file, open, onClose, onDone, defaultRatio = "16:9", outWidth = 1600 }) {
  const [img, setImg] = useState(null);
  const [ratioK, setRatioK] = useState(defaultRatio);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const frameRef = useRef(null);
  const drag = useRef(null);
  const ratio = RATIOS.find((r) => r.k === ratioK)?.v || 16 / 9;

  useEffect(() => {
    if (!file) return setImg(null);
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => { setImg(im); setZoom(1); setPos({ x: 0, y: 0 }); };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Dimensioni del frame (larghezza disponibile) e dell'immagine "cover" a zoom 1
  const fw = 320, fh = Math.round(fw / ratio);
  const base = img ? Math.max(fw / img.width, fh / img.height) : 1;
  const dw = img ? img.width * base * zoom : 0, dh = img ? img.height * base * zoom : 0;
  const clamp = (p, z = zoom) => {
    const w = img.width * base * z, h = img.height * base * z;
    return { x: Math.min(0, Math.max(fw - w, p.x)), y: Math.min(0, Math.max(fh - h, p.y)) };
  };
  useEffect(() => { if (img) setPos((p) => clamp(p)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [zoom, ratioK, img]);

  const onDown = (e) => { e.preventDefault(); drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (!drag.current) return; setPos(clamp({ x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) })); };
  const onUp = () => { drag.current = null; };

  const confirm = () => {
    const scale = outWidth / fw;
    const c = document.createElement("canvas"); c.width = outWidth; c.height = Math.round(fh * scale);
    const ctx = c.getContext("2d");
    ctx.drawImage(img, pos.x * scale, pos.y * scale, dw * scale, dh * scale);
    c.toBlob((b) => onDone(b), "image/jpeg", 0.9);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="image-crop-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Crop className="h-5 w-5" /> Ritaglia immagine</DialogTitle></DialogHeader>
        <div className="flex gap-1.5 flex-wrap" data-testid="image-crop-ratios">
          {RATIOS.map((r) => <button key={r.k} type="button" onClick={() => setRatioK(r.k)} className={`text-[11px] px-2.5 py-1 rounded-full border ${ratioK === r.k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-600"}`} data-testid={`image-crop-ratio-${r.k}`}>{r.label}</button>)}
        </div>
        <div ref={frameRef} className="mx-auto relative overflow-hidden rounded-xl bg-zinc-200 ring-2 ring-zinc-900 cursor-move select-none" style={{ width: fw, height: fh, touchAction: "none" }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="image-crop-frame">
          {img && <img src={img.src} alt="" draggable={false} className="absolute max-w-none pointer-events-none" style={{ width: dw, height: dh, left: pos.x, top: pos.y }} />}
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600">Zoom
          <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1" data-testid="image-crop-zoom" />
        </label>
        <p className="text-[11px] text-zinc-500">Trascina l'immagine per inquadrarla. L'esportazione è a {outWidth}px di larghezza.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onDone(null)} className="flex-1" data-testid="image-crop-skip">Usa originale</Button>
          <Button onClick={confirm} disabled={!img} className="flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]" data-testid="image-crop-confirm">Ritaglia e carica</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
